// Open the focused assistant route with a durable scoped session; late results cannot leave their original workspace, page or filters.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { canOpenAssistantResult, type AssistantRequestOrigin } from './assistantRequestOrigin';

interface CreateSessionRequest {
  origin: AssistantRequestOrigin;
  scope: Record<string,string>;
  language: 'en' | 'zh';
  seedMessage?: string;
}

export function AskAiButton({
  className,
  seedMessage,
  label,
  scope:explicitScope,
}: {
  className?: string;
  seedMessage?: string;
  label?: string;
  scope?: Record<string,string>;
}) {
  const { t,i18n } = useTranslation();
  const location=useLocation(),navigate=useNavigate(),ws=useWorkspaceStore(s=>s.current?.id);
  const [session,setSession]=useState<{key:string;id:string}|null>(null);
  const params=new URLSearchParams(location.search),scope:Record<string,string>={};
  for(const key of ['from','to','user_id','recorded_by','category_id','merchant_normalized','currency','valuation_status','group_id','exclude_group_id','q'])if(params.get(key))scope[key]=params.get(key)!;
  if(params.get('spender_user_id'))scope.user_id=params.get('spender_user_id')!;
  if(location.pathname.startsWith('/receipts/'))scope.receipt_id=decodeURIComponent(location.pathname.split('/')[2]);
  if(location.pathname.startsWith('/shopping/'))scope.shopping_list_id=decodeURIComponent(location.pathname.split('/')[2]);
  Object.assign(scope,explicitScope);const key=JSON.stringify([ws,location.pathname,scope]);
  const mounted=useRef(false),pending=useRef<AssistantRequestOrigin|null>(null);
  const current=useRef<AssistantRequestOrigin>({workspaceId:ws,pathname:location.pathname,search:location.search,scopeKey:key,revision:0});
  if(current.current.workspaceId!==ws||current.current.pathname!==location.pathname||current.current.search!==location.search||current.current.scopeKey!==key){
    current.current={workspaceId:ws,pathname:location.pathname,search:location.search,scopeKey:key,revision:current.current.revision+1};
  }
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  const isCurrent=(origin:AssistantRequestOrigin)=>canOpenAssistantResult(origin,current.current,{
    mounted:mounted.current,workspaceId:useWorkspaceStore.getState().current?.id,pathname:window.location.pathname,search:window.location.search,
  });
  const openChat=(id:string,request:CreateSessionRequest)=>{
    if(!isCurrent(request.origin))return;
    const search=new URLSearchParams({session:id,returnTo:request.origin.pathname+request.origin.search});
    if(request.seedMessage)search.set('message',request.seedMessage);
    navigate(`/assistant/chat?${search}`);
  };
  const create=useMutation({
    mutationFn:(request:CreateSessionRequest)=>{
      if(!isCurrent(request.origin))throw new Error('Conversation source changed');
      return api.post<{session:{id:string}}>('/chat/session',{language:request.language,scope:request.scope});
    },
    onSuccess:(result,request)=>{
      if(!isCurrent(request.origin))return;
      setSession({key:request.origin.scopeKey,id:result.session.id});
      openChat(result.session.id,request);
    },
    onSettled:(_result,_error,request)=>{if(pending.current===request.origin)pending.current=null;},
  });
  const ask=()=>{
    if(!isCurrent(current.current))return;
    const request:CreateSessionRequest={origin:current.current,scope:{...scope},language:i18n.language.startsWith('en')?'en':'zh',seedMessage};
    if(session?.key===key){openChat(session.id,request);return;}
    if(pending.current)return;
    pending.current=request.origin;
    create.mutate(request);
  };
  return (
    <>
      <button
        type="button"
        disabled={create.isPending}
        onClick={ask}
        className={
          className ??
          'inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-md border bg-background px-3 text-sm hover:bg-muted'
        }
      >
        {create.isPending?<Loader2 className="h-4 w-4 animate-spin"/>:'💬'} {label ?? t('chat.askAi')}
      </button>
      {create.error&&create.variables&&isCurrent(create.variables.origin)&&<span role="alert" className="text-xs text-destructive">{create.error.message}</span>}
    </>
  );
}
